import {
  parseReturnHistoryNotes,
  type ParsedReturnHistoryNotes,
} from '../../../../utils/returnHistoryNotes.utils';

/** Marker written by backend when an exchange is reversed after SOL reject. */
export const EXCHANGE_OUTCOME_SOLICITUD_RECHAZADA = 'EXCHANGE_OUTCOME:SOLICITUD_RECHAZADA';

export const isExchangeRejectedByRequest = (notes?: string | null): boolean =>
  (notes ?? '').includes(EXCHANGE_OUTCOME_SOLICITUD_RECHAZADA);

/** Strip internal exchange markers from notes shown in the UI. */
export const stripExchangeOutcomeMarkers = (notes?: string | null): string | undefined => {
  if (notes == null || notes.trim() === '') {
    return undefined;
  }

  const cleaned = notes
    .split('\n')
    .map((line) => line.trim())
    .filter(
      (line) =>
        line.length > 0 &&
        !line.startsWith('EXCHANGE_OUTCOME:') &&
        !line.startsWith('EXCHANGE_LINE_SNAPSHOT:'),
    )
    .join('\n')
    .trim();

  return cleaned.length > 0 ? cleaned : undefined;
};

export interface ProductHistoryItem {
  id: number;
  actionType: string;
  exchangeId?: string | null;
  returnId?: string | null;
  product: {
    id: number;
    name: string;
  };
  warehouseInventoryProduct?: {
    id?: number;
    productCodeUnique?: string;
  };
  originalPrice: number;
  finalPrice: number;
  discountType?: string;
  discountValue?: number;
  notes?: string;
  createdAt: string;
  processedBy?: {
    username: string;
  };
}

export type HistoryTimelineEntry =
  | { kind: 'single'; item: ProductHistoryItem }
  | {
      kind: 'exchange';
      exchangeId: string;
      outItems: ProductHistoryItem[];
      inItems: ProductHistoryItem[];
      createdAt: string;
      processedByUsername: string;
      notes?: string;
      /** True when the remote SOL was rejected and the exchange was reversed. */
      rejectedByRequest: boolean;
    }
  | {
      kind: 'return';
      returnId: string;
      items: ProductHistoryItem[];
      isPartial: boolean;
      createdAt: string;
      processedByUsername: string;
      notes?: string;
      returnMeta: ParsedReturnHistoryNotes;
      orderRefundAmount: number;
    };

const isExchangeLine = (item: ProductHistoryItem): boolean =>
  item.actionType === 'EXCHANGED_OUT' || item.actionType === 'EXCHANGED_IN';

const isReturnLine = (item: ProductHistoryItem): boolean =>
  item.actionType === 'RETURNED' || item.actionType === 'REFUNDED';

export const toMoney = (value: number | string | undefined): number => {
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

export const buildProductHistoryTimeline = (
  history: ProductHistoryItem[],
): HistoryTimelineEntry[] => {
  const exchangeGroups = new Map<string, ProductHistoryItem[]>();
  const returnGroups = new Map<string, ProductHistoryItem[]>();
  const singles: ProductHistoryItem[] = [];

  for (const item of history) {
    if (item.returnId && isReturnLine(item)) {
      const group = returnGroups.get(item.returnId) ?? [];
      group.push(item);
      returnGroups.set(item.returnId, group);
    } else if (item.exchangeId && isExchangeLine(item)) {
      const group = exchangeGroups.get(item.exchangeId) ?? [];
      group.push(item);
      exchangeGroups.set(item.exchangeId, group);
    } else {
      singles.push(item);
    }
  }

  const exchangeEntries: HistoryTimelineEntry[] = [];

  exchangeGroups.forEach((items, exchangeId) => {
    const outItems = items.filter((i) => i.actionType === 'EXCHANGED_OUT');
    const inItems = items.filter((i) => i.actionType === 'EXCHANGED_IN');
    const sorted = [...items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const anchor = sorted[0];

    exchangeEntries.push({
      kind: 'exchange',
      exchangeId,
      outItems,
      inItems,
      createdAt: anchor.createdAt,
      processedByUsername: anchor.processedBy?.username ?? '—',
      notes: stripExchangeOutcomeMarkers(anchor.notes),
      rejectedByRequest: items.some((item) => isExchangeRejectedByRequest(item.notes)),
    });
  });

  const returnEntries: HistoryTimelineEntry[] = [];

  returnGroups.forEach((items, returnId) => {
    const sorted = [...items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const anchor = sorted[0];
    const returnMeta = parseReturnHistoryNotes(anchor.notes);
    const isPartial =
      returnMeta.isPartial ?? (anchor.notes ?? '').toLowerCase().includes('devolución parcial');

    const legacyRefundTotal = sorted.reduce((sum, item) => sum + toMoney(item.finalPrice), 0);
    const perItemRefunds = sorted.map((item) => parseReturnHistoryNotes(item.notes).refundAmount);
    const hasPerItemRefunds =
      perItemRefunds.length > 0 && perItemRefunds.every((amount) => amount != null && amount >= 0);
    const perItemRefundTotal = hasPerItemRefunds
      ? roundMoney(
          sorted.reduce((sum, item) => {
            const refund = parseReturnHistoryNotes(item.notes).refundAmount ?? 0;
            return sum + refund;
          }, 0),
        )
      : undefined;
    const hasStructuredOrderRefund =
      (anchor.notes ?? '').trim().startsWith('{') &&
      returnMeta.orderRefundAmount != null &&
      returnMeta.orderRefundAmount > 0;
    const orderRefundAmount =
      perItemRefundTotal ??
      (hasStructuredOrderRefund ? returnMeta.orderRefundAmount! : legacyRefundTotal);

    returnEntries.push({
      kind: 'return',
      returnId,
      items: sorted,
      isPartial,
      createdAt: anchor.createdAt,
      processedByUsername: anchor.processedBy?.username ?? '—',
      notes: anchor.notes,
      returnMeta,
      orderRefundAmount,
    });
  });

  const entries: HistoryTimelineEntry[] = [
    ...exchangeEntries,
    ...returnEntries,
    ...singles.map((item) => ({ kind: 'single' as const, item })),
  ];

  entries.sort((a, b) => {
    const dateA = a.kind === 'exchange' || a.kind === 'return' ? a.createdAt : a.item.createdAt;
    const dateB = b.kind === 'exchange' || b.kind === 'return' ? b.createdAt : b.item.createdAt;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return entries;
};

export const getExchangeSummaryLabel = (outCount: number, inCount: number): string => {
  const parts: string[] = [];
  if (outCount > 0) {
    parts.push(`${outCount} a intercambiar`);
  }
  if (inCount > 0) {
    parts.push(`${inCount} nueva${inCount === 1 ? '' : 's'} en la orden`);
  }
  return parts.join(' · ') || 'Sin piezas';
};

/** Historial de orden acotado a una pieza (incluye bloques de intercambio relacionados). */
export const filterHistoryForWarehouseProduct = (
  history: ProductHistoryItem[],
  warehouseInventoryProductId: number,
): ProductHistoryItem[] => {
  const matchesWip = (item: ProductHistoryItem) =>
    item.warehouseInventoryProduct?.id === warehouseInventoryProductId ||
    (item as ProductHistoryItem & { warehouseInventoryProductId?: number })
      .warehouseInventoryProductId === warehouseInventoryProductId;

  const exchangeIds = new Set<string>();
  for (const item of history) {
    if (item.exchangeId && matchesWip(item)) {
      exchangeIds.add(item.exchangeId);
    }
  }

  return history.filter((item) => {
    if (matchesWip(item)) return true;
    if (item.exchangeId && exchangeIds.has(item.exchangeId)) return true;
    return false;
  });
};

export const mapSoldPieceHistoryToProductHistory = (
  items: Array<{
    id: number;
    actionType: string;
    exchangeId?: string | null;
    returnId?: string | null;
    productName: string;
    warehouseInventoryProductId?: number | null;
    productCodeUnique?: string | null;
    originalPrice: number;
    finalPrice: number;
    discountType?: string | null;
    discountValue?: number | null;
    notes?: string | null;
    createdAt: string;
    processedByUsername?: string | null;
  }>,
): ProductHistoryItem[] =>
  items.map((h) => ({
    id: h.id,
    actionType: h.actionType,
    exchangeId: h.exchangeId,
    returnId: h.returnId,
    product: { id: 0, name: h.productName },
    warehouseInventoryProduct: {
      id: h.warehouseInventoryProductId ?? undefined,
      productCodeUnique: h.productCodeUnique ?? undefined,
    },
    originalPrice: h.originalPrice,
    finalPrice: h.finalPrice,
    discountType: h.discountType ?? undefined,
    discountValue: h.discountValue ?? undefined,
    notes: h.notes ?? undefined,
    createdAt: h.createdAt,
    processedBy: h.processedByUsername ? { username: h.processedByUsername } : undefined,
  }));

export const getReturnSummaryLabel = (
  itemCount: number,
  refundTotal: number,
  formatCurrency: (amount: number) => string,
  isPartial: boolean,
): string => {
  const scope = isPartial ? 'parcial' : 'total';
  return `${itemCount} pieza${itemCount === 1 ? '' : 's'} · Reembolso ${scope}: ${formatCurrency(refundTotal)}`;
};
